import CupIcon from "@/assets/cup.svg";
import EyeIcon from "@/assets/eye.svg";
import FavIcon from "@/assets/heart.svg";
import MsgIcon from "@/assets/msg-none.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import TournamentIcon from "@/assets/tournaments.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { matchAndClubCountryCodes } from "@teamz/common";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";
import type { Routes } from "@/routes";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useOpenMessageThread } from "@/hooks/use-open-message-thread";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { routes } from "@/routes";

export function PlayerListItem({ player, fromPath, onClick, className, availability = "matchs" }: {
  player: RouterOutputs["player"]["listPlayers"][number];
  fromPath: Routes["clubs"] | Routes["favorites"] | Routes["admin"] | Routes["players"];
  onClick?: () => void;
  className?: string;
  availability?: "matchs" | "tournaments";
}) {
  const { t } = useTranslation(["common", "profile"]);
  const actualYear = new Date().getFullYear();
  const breakpoint = useBreakpoints();
  const navigate = useNavigate();
  const openMessageThread = useOpenMessageThread();
  const { user } = useMe();
  const location = useLocation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: club } = useQuery(trpc.club.getClub.queryOptions(
    { clubId: user.id },
    { enabled: !location.pathname.includes(routes.admin) },
  ));

  const { mutate } = useMutation(trpc.club.updateFavoritesPlayers.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      queryClient.invalidateQueries(trpc.club.getClubFavoritePlayers.queryFilter());
    },
  }));

  const isPlayerAlreadyFav = club?.favoritesPlayers?.includes(player.id);

  const updateFavorite = () => {
    mutate({
      playerId: player.id,
    });
  };

  function upsertThread() {
    openMessageThread(player.userId);
  }

  function handleRedirect() {
    if (fromPath === routes.admin) {
      navigate(routes.getAdminPlayerProfilePath(player.userId), { state: { fromPath } });
    }
    else {
      navigate(routes.getPlayerPath(player.userId), { state: { fromPath } });
    }
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        "grid grid-cols-1 gap-2 rounded-[18px] border bg-white p-2.5 md:grid-cols-2 md:rounded-none md:border-x-0 md:border-t-0 md:first:rounded-t-[18px] md:last:rounded-b-[18px] md:last:border-b-0 lg:grid-cols-[1fr_auto_auto_242px_auto] lg:gap-3",
        className,
      )}
    >
      <div
        className="order-1 flex cursor-pointer items-center gap-2"
        onClick={(fromPath === routes.players || fromPath === routes.favorites) ? handleRedirect : undefined}
      >
        <img
          src={player.imgUrl ?? undefined}
          alt={`Profil de ${player.firstName}`}
          className="pointer-events-none size-14 shrink-0 select-none rounded-xl object-cover"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="line-clamp-1 text-ellipsis text-lg font-semibold">
            {player.fullName}
          </h1>
          <div className="flex items-center gap-2">
            <div className="flex w-auto max-w-48 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5 lg:hidden">
              <CircleFlag countryCode={player.nationalityCode.toLowerCase()} className="size-4 rounded-full" />
              <span className="pr-2 text-sm font-medium">
                {player.nationalityCode}
              </span>
            </div>

            {player.gender === "male"
              ? (
                  <Badge variant="blue-dark" size="compact">
                    H
                  </Badge>
                )
              : (
                  <Badge variant="purple-dark" size="compact">
                    F
                  </Badge>
                )}

            <span className="text-nowrap text-sm font-medium text-neutral-500">
              {t("profile:years", { age: player.age })}
            </span>

            {player.isNew && <Badge variant="orange">{t("common:new")}</Badge>}
          </div>
        </div>
      </div>

      <div className="order-2 hidden items-center justify-center lg:flex">
        <div className="flex h-fit w-auto max-w-48 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5">
          <CircleFlag countryCode={player.nationalityCode.toLowerCase()} className="size-4 rounded-full" />
          <span className="pr-2 text-sm font-medium">
            {player.nationalityCode}
          </span>
        </div>
      </div>

      <div className="order-3 flex divide-x divide-neutral-400 rounded-lg border lg:w-[220px]">
        { player.FFTRanking
          ? (
              player.FFTRanking === "T100"
                ? (
                    <div className="flex flex-1 flex-col-reverse items-center justify-center gap-0 p-3 lg:py-1">
                      <span className="text-sm font-semibold text-neutral-500 lg:text-xs">FFT</span>
                      <span className="font-bold">
                        N
                        {player.FFTRankingNumber}
                      </span>
                    </div>
                  )
                : (
                    <div className="flex flex-1 flex-col-reverse items-center justify-center gap-0 p-3 lg:py-1">
                      <span className="text-sm font-semibold text-neutral-500 lg:text-xs">FFT</span>
                      <span className="font-bold">{player.FFTRanking}</span>
                    </div>
                  )
            )
          : (
              <div className="flex flex-1 items-center justify-between gap-2 rounded-l-lg bg-neutral-50 p-3 text-neutral-450 lg:justify-center">
                <span className="text-sm font-semibold lg:hidden">FFT</span>
                <DotsIcon />
              </div>
            )}
        { player.UTRRanking
          ? (
              <div className="flex flex-1 flex-col-reverse items-center justify-center gap-0 p-3 lg:py-1">
                <span className="text-sm font-semibold text-neutral-500 lg:text-xs">UTR</span>
                <span className="font-bold">{player.UTRRanking}</span>
              </div>
            )
          : (
              <div className="flex flex-1 items-center justify-between gap-2 bg-neutral-50 p-3 text-neutral-450 lg:justify-center">
                <span className="text-sm font-semibold lg:hidden">UTR</span>
                <DotsIcon />
              </div>
            )}
        { player.ATPWTARanking
          ? (
              <div className="flex flex-1 flex-col-reverse items-center justify-center gap-0 p-3 lg:py-1">
                <span className="text-sm font-semibold text-neutral-500 lg:text-xs">{player.gender === "female" ? "WTA" : "ATP"}</span>
                <span className="font-bold">{player.ATPWTARanking}</span>
              </div>
            )
          : (
              <div className="flex flex-1 items-center justify-between gap-2 rounded-r-lg bg-neutral-50 p-3 text-neutral-450 lg:justify-center">
                <span className="text-sm font-semibold lg:hidden">{player.gender === "female" ? "WTA" : "ATP"}</span>
                <DotsIcon />
              </div>
            )}
      </div>

      {availability === "matchs" && (
        <div className="order-4 flex shrink-0 flex-col rounded-xl border p-1.5 md:order-2 lg:order-4 lg:w-fit lg:justify-center lg:p-1">
          <div className="flex items-center justify-between px-2 py-1.5 lg:py-0">
            <div className="flex items-center gap-1 lg:hidden">
              <CupIcon className="text-orange-500" />
              <span className="text-sm font-medium">{t("profile:match")}</span>
            </div>
            {
              player.matchAvailabilities === "available"
                ? (
                    <span className="text-sm font-medium text-orange-500">
                      {t("profile:available")}
                    </span>
                  )
                : player.matchAvailabilities === "not-available"
                  ? (
                      (
                        <span className="text-sm font-medium text-neutral-450">
                          {t("profile:not-available")}
                        </span>
                      )
                    )
                  : (
                      (
                        <span className="text-sm font-medium text-neutral-500">
                          {t("profile:not-confirmed")}
                        </span>
                      )
                    )
            }
          </div>

          <div className="flex w-full items-center justify-between px-2 lg:justify-start ">
            <span className="shrink-0 text-sm font-medium text-neutral-700 lg:hidden">
              {t("profile:season", { season: actualYear })}
            </span>
            <div className="flex items-center gap-2">
              {matchAndClubCountryCodes.map(code => (
                <div key={code}>
                  <CircleFlag
                    countryCode={code}
                    className={cn("pointer-events-none block size-6 rounded-full border-2 border-white opacity-15 shadow-md", player?.matchAvailabilitiesCountries?.includes(code) && "opacity-100")}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {availability === "tournaments" && (
        <div className="order-4 flex shrink-0 items-center justify-between gap-4 rounded-xl border px-2 py-3 md:order-2 lg:order-4 lg:py-0">
          <div className="flex items-center gap-1">
            <TournamentIcon className="text-orange-500" />
            <span className="text-sm font-medium">{t("profile:tournament")}</span>
          </div>
          {
            player.tournamentAvailabilities === "available"
              ? (
                  <span className="text-sm font-medium text-orange-500">
                    {t("profile:available")}
                  </span>
                )
              : player.tournamentAvailabilities === "not-available"
                ? (
                    (
                      <span className="text-sm font-medium text-neutral-450">
                        {t("profile:not-available")}
                      </span>
                    )
                  )
                : (
                    (
                      <span className="text-sm font-medium text-neutral-500">
                        {t("profile:not-confirmed")}
                      </span>
                    )
                  )
          }
        </div>
      )}

      {(fromPath === routes.players || fromPath === routes.favorites) && (
        <div className="order-5 flex items-center gap-1">
          <Button
            variant="outline"
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
            onClick={updateFavorite}
            className="active:border-neutral-300"
          >
            <FavIcon
              className={
                isPlayerAlreadyFav
                  ? "fill-[#FE5000] "
                  : "fill-white "
              }
            />
            <span className="hidden sm:inline-flex md:hidden">{t("common:mobile-nav.favorites")}</span>
          </Button>
          <Button
            variant="outline"
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
            onClick={() => upsertThread()}
          >
            <MsgIcon />
            <span className="hidden sm:inline-flex md:hidden">Message</span>
          </Button>
          <Button
            className="w-full lg:w-fit"
            size={["lg", "xl"].includes(breakpoint) ? "compact" : "default"}
            onClick={handleRedirect}
          >
            <EyeIcon />
            <span className="lg:hidden">{t("common:go-to-player-profile")}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
